import { useState, useEffect } from 'react'
import api from '@/services/api'

interface FeatureFlags {
  consumerDutyIntegration: boolean
  aiAnalytics: boolean
  advancedReporting: boolean
}

const DEFAULT_FLAGS: FeatureFlags = {
  consumerDutyIntegration: false,
  aiAnalytics: false,
  advancedReporting: false,
}

// Cache the feature flags to avoid repeated API calls
let cachedFlags: FeatureFlags | null = null
let cachePromise: Promise<FeatureFlags> | null = null

async function fetchFeatureFlags(): Promise<FeatureFlags> {
  // Return cached flags if available
  if (cachedFlags) {
    return cachedFlags
  }

  // Return existing promise if fetch is in progress
  if (cachePromise) {
    return cachePromise
  }

  // Fetch from API
  cachePromise = api
    .get('/health/detailed')
    .then((response) => {
      const features = response.data?.features || {}
      cachedFlags = {
        consumerDutyIntegration: features.consumer_duty_integration ?? false,
        aiAnalytics: features.ai_analytics ?? false,
        advancedReporting: features.advanced_reporting ?? false,
      }
      return cachedFlags
    })
    .catch((error) => {
      cachedFlags = DEFAULT_FLAGS
      return DEFAULT_FLAGS
    })
    .finally(() => {
      cachePromise = null
    })

  return cachePromise
}

export function useFeatureFlags() {
  const [flags, setFlags] = useState<FeatureFlags>(cachedFlags || DEFAULT_FLAGS)
  const [loading, setLoading] = useState(!cachedFlags)

  useEffect(() => {
    let mounted = true

    fetchFeatureFlags().then((fetchedFlags) => {
      if (mounted) {
        setFlags(fetchedFlags)
        setLoading(false)
      }
    })

    return () => {
      mounted = false
    }
  }, [])

  return { flags, loading }
}

// Export a function to clear the cache (useful for testing or after config changes)
export function clearFeatureFlagsCache() {
  cachedFlags = null
  cachePromise = null
}
